/**
 * Chatbot.jsx — Saksham AI Chat
 *
 * Limits:
 *  Free : 5 messages/day (server-enforced, mirrored client-side for instant feedback)
 *  Paid : Unlimited
 *
 * Memory:
 *  Free : Rolling 6-message window, no cross-session memory
 *  Paid : Rolling window + running summary preserved in sessionStorage
 *
 * Token guard: max ~375 words per message (500 tokens) — server-enforced
 *
 * ─── RAZORPAY ─────────────────────────────────────────────────────────────
 * Upgrade CTA buttons call navigate('/pricing').
 * Replace with Razorpay checkout when integrating payments.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate }           from 'react-router-dom';
import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from 'react-icons/md';
import { FaUserCircle }                from 'react-icons/fa';
import { auth }                        from '../firebase';
import { signOut }                     from 'firebase/auth';
import { authFetch, getUserClaims }    from '../utils/authFetch';
import '../styles/global.css';

const FREE_MSG_LIMIT   = 5;   // must match server LIMITS.chatMsgsPerDay
const FREE_TOKEN_LIMIT = 800; // must match server LIMITS.chatTokensPerDay
const FREE_WORD_LIMIT  = Math.floor(FREE_TOKEN_LIMIT * 0.75); // ~600 words

export default function Chatbot() {
  const navigate = useNavigate();

  // ─── Chat state ──────────────────────────────────────────────────────────
  const [messages, setMessages] = useState([{
    text  : 'Hi there! 💙 I\'m Saksham, your mental wellness companion from HealHub. I\'m here to listen, support, and walk alongside you. How are you feeling today?',
    sender: 'bot',
  }]);
  const [inputText,    setInputText]    = useState('');
  const [loading,      setLoading]      = useState(false);
  const [isListening,  setIsListening]  = useState(false);

  // ─── Rolling memory (sent to server each turn) ───────────────────────────
  const [sessionHistory, setSessionHistory] = useState([]);
  // sessionSummary — how memory works per tier:
  //  Free : sessionStorage only → clears on tab close
  //  Paid : loaded from Firestore on mount via server, saved back after every 6 turns
  //         The server stores it in users/{uid}/sakshamMemory/latest
  //         So paid users have memory across devices, browsers, and days
  const [sessionSummary, setSessionSummary] = useState(
    sessionStorage.getItem('saksham_summary') || ''
  );
  const [memoryLoaded, setMemoryLoaded] = useState(false);

  // ─── Usage / limits — both guards tracked ───────────────────────────────
  const [msgUsed,      setMsgUsed]      = useState(0);
  const [tokensUsed,   setTokensUsed]   = useState(0);
  const [isPaid,       setIsPaid]       = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [usageLoaded,  setUsageLoaded]  = useState(false);

  // ─── Theme + navbar ──────────────────────────────────────────────────────
  const [theme,          setTheme]          = useState(localStorage.getItem('theme') || 'light-theme');
  const [showInsightMenu, setShowInsightMenu] = useState(false);
  const hideTimeout    = useRef(null);

  // ─── Refs ────────────────────────────────────────────────────────────────
  const recognitionRef = useRef(null);
  const chatWindowRef  = useRef(null);

  // ─── Init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.className = theme;
  }, [theme]);

  useEffect(() => {
    const load = async () => {
      try {
        const claims = await getUserClaims();
        setIsPaid(claims.paid);

        // Fetch usage stats
        const res  = await authFetch('/api/usage');
        const data = await res.json();
        const msgs   = data.chat?.msgsUsed   || 0;
        const tokens = data.chat?.tokensUsed || 0;
        setMsgUsed(msgs);
        setTokensUsed(tokens);
        if (!claims.paid && (msgs >= FREE_MSG_LIMIT || tokens >= FREE_TOKEN_LIMIT)) {
          setLimitReached(true);
        }

        // ── Cross-session memory for paid users ──────────────────────────
        // Fetch the persisted Firestore summary if sessionStorage is empty.
        // This restores Saksham's memory of previous conversations after a
        // tab close, browser restart, or different device login.
        if (claims.paid && !sessionStorage.getItem('saksham_summary')) {
          try {
            const memRes  = await authFetch('/api/chat/memory');
            const memData = await memRes.json();
            if (memData.summary) {
              setSessionSummary(memData.summary);
              sessionStorage.setItem('saksham_summary', memData.summary);
            }
          } catch { /* memory load failing is non-critical */ }
        }
      } catch { /* silent */ }
      finally { setUsageLoaded(true); setMemoryLoaded(true); }
    };
    if (auth.currentUser) load();
  }, []);

  // ─── Scroll to bottom ────────────────────────────────────────────────────
  useEffect(() => {
    if (chatWindowRef.current)
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
  }, [messages]);

  // ─── Speech recognition ──────────────────────────────────────────────────
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (SpeechRecognition && !recognitionRef.current) recognitionRef.current = new SpeechRecognition();

  useEffect(() => {
    if (!recognitionRef.current) return;
    navigator.mediaDevices.getUserMedia({ audio: true }).then(() => {
      const r = recognitionRef.current;
      r.continuous = true; r.interimResults = true; r.lang = 'en-IN';
      r.onresult = (e) => setInputText(Array.from(e.results).map(res => res[0].transcript).join(''));
    }).catch(() => {});
  }, []);

  const toggleListening = () => {
    const r = recognitionRef.current; if (!r) return;
    isListening ? r.stop() : r.start();
    setIsListening(p => !p);
  };

  // ─── Navbar ──────────────────────────────────────────────────────────────
  const toggleTheme = () => {
    const themes = ['light-theme', 'dark-theme', 'zen-theme'];
    const next   = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(next); localStorage.setItem('theme', next); document.body.className = next;
  };
  const handleLogout = async () => { await signOut(auth); navigate('/'); };
  const handleInsightMouseEnter = () => { clearTimeout(hideTimeout.current); setShowInsightMenu(true); };
  const handleInsightMouseLeave = () => { hideTimeout.current = setTimeout(() => setShowInsightMenu(false), 150); };
  const handleUpgrade = () => navigate('/pricing'); // RAZORPAY: replace with checkout

  // ─── Word count for message guard ────────────────────────────────────────
  const wordCount  = inputText.trim().split(/\s+/).filter(Boolean).length;
  const MAX_WORDS  = 375; // ~500 tokens
  const msgTooLong = wordCount > MAX_WORDS;

  // ─── Remaining indicators — show whichever is more constrained ──────────
  const msgsRemaining   = Math.max(0, FREE_MSG_LIMIT   - msgUsed);
  const tokensRemaining = Math.max(0, FREE_TOKEN_LIMIT - tokensUsed);
  const wordsRemaining  = Math.floor(tokensRemaining * 0.75);

  // Progress bar uses whichever guard is closer to exhaustion
  const msgPct   = Math.min(100, (msgUsed    / FREE_MSG_LIMIT)   * 100);
  const tokenPct = Math.min(100, (tokensUsed / FREE_TOKEN_LIMIT) * 100);
  const usagePct = Math.max(msgPct, tokenPct); // show the worse of the two
  const usageColor = usagePct >= 100 ? '#ef5350' : usagePct >= 60 ? '#ff9800' : '#4caf50';

  // Estimate tokens in current input for real-time feedback
  const currentMsgTokens = Math.ceil((inputText || '').length / 4);

  // ─── Send message ────────────────────────────────────────────────────────
  const handleSendMessage = async () => {
    if (!inputText.trim() || loading) return;

    if (!auth.currentUser) {
      setMessages(p => [...p, { text: 'Please log in to chat with Saksham. 💙', sender: 'bot' }]);
      return;
    }

    // Client-side limit check (server also enforces — this is for instant UX feedback)
    if (limitReached && !isPaid) {
      setMessages(p => [...p, {
        text  : `You've used all ${FREE_MSG_LIMIT} free messages with Saksham for today. Come back tomorrow, or upgrade to ₹35/month for unlimited conversations. 💙`,
        sender: 'bot',
      }]);
      return;
    }

    if (msgTooLong) {
      setMessages(p => [...p, {
        text  : `Your message is a bit long (${wordCount} words). Please keep it under ${MAX_WORDS} words so I can respond thoughtfully. 💙`,
        sender: 'bot',
      }]);
      return;
    }

    const userMsg = { text: inputText, sender: 'user' };
    setMessages(p => [...p, userMsg]);
    const msgToSend = inputText;
    setInputText('');
    setLoading(true);

    // Build rolling window (last 6 messages for API)
    const apiHistory = sessionHistory.slice(-6).map(m => ({
      role   : m.sender === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    try {
      const res  = await authFetch('/api/chat', {
        method: 'POST',
        body  : JSON.stringify({ message: msgToSend, history: apiHistory, sessionSummary }),
      });
      const data = await res.json();

      if (res.status === 429) {
        setLimitReached(true);
        setMsgUsed(FREE_MSG_LIMIT);
        setMessages(p => [...p, {
          text  : data.error || `You've used today's ${FREE_MSG_LIMIT} free messages. Upgrade to continue. 💙`,
          sender: 'bot',
        }]);
        return;
      }

      if (!res.ok) {
        setMessages(p => [...p, { text: data.error || 'Something went wrong. Please try again.', sender: 'bot' }]);
        return;
      }

      const botMsg = { text: data.message, sender: 'bot', isCrisis: data.isCrisis };
      setMessages(p => [...p, botMsg]);

      // Update rolling history (keep last 12 turns in memory = 6 exchanges)
      setSessionHistory(p => [...p, userMsg, botMsg].slice(-12));

      // Update rolling session summary
      // - All users: keep in React state + sessionStorage for tab-level persistence
      // - Paid users: server already saved to Firestore (handled in /api/chat route)
      //   sessionStorage is just the fast local cache for this tab
      if (data.sessionSummary) {
        setSessionSummary(data.sessionSummary);
        sessionStorage.setItem('saksham_summary', data.sessionSummary);
      }

      // Update local usage counts — server is authoritative, this is just instant UX
      if (!isPaid) {
        const newMsgs   = msgUsed + 1;
        const newTokens = tokensUsed + currentMsgTokens;
        setMsgUsed(newMsgs);
        setTokensUsed(newTokens);
        if (newMsgs >= FREE_MSG_LIMIT || newTokens >= FREE_TOKEN_LIMIT) {
          setLimitReached(true);
        }
      }

    } catch {
      setMessages(p => [...p, { text: 'Saksham is momentarily unavailable. Please try again. 💙', sender: 'bot' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">

      {/* ─── NAVBAR ──────────────────────────────────────────────────────── */}
      <nav className="navbar">
        <div className="navbar-left">
          <Link to="/">Home</Link>
          <Link to="/resources">Resources</Link>
          <Link to="/community">Community</Link>
          <Link to="/feed" className="nav-item">Feed</Link>
          <Link to="/chatbot" className="nav-item active">Chat with Saksham</Link>
          <div className="nav-dropdown" onMouseEnter={handleInsightMouseEnter} onMouseLeave={handleInsightMouseLeave}>
            <span className="nav-item">Insight ▾</span>
            {showInsightMenu && (
              <div className="dropdown-menu">
                <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                <Link to="/insight" className="dropdown-link">Look at Report</Link>
              </div>
            )}
          </div>
          <Link to="/chattr" className="nav-item">Chattr</Link>
        </div>
        <div className="navbar-center">
          <span className="healhub-brand">💖🌿 HealHub</span>
          <Link to="/about" className="nav-item">About Us</Link>
        </div>
        <div className="navbar-right">
          <button onClick={toggleTheme} className="theme-switcher-btn">
            {theme === 'light-theme' ? <MdOutlineLightMode /> : theme === 'dark-theme' ? <MdModeNight /> : <MdFilterVintage />}
          </button>
          <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24} /></Link>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </nav>

      {/* ─── CHAT CARD ───────────────────────────────────────────────────── */}
      <div className="card chatbot-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h2>💬 Chat with Saksham AI</h2>
            <p className="chat-intro" style={{ marginBottom: '8px', color: 'var(--text-secondary-color)' }}>
              Saksham is your mental wellness companion — trained in CBT, ACT & mindfulness. He's here to truly listen. 💙
            </p>
          </div>
          <div style={{
            padding: '6px 14px', borderRadius: '20px',
            background: isPaid ? '#e8f5e9' : '#e3f2fd',
            color: isPaid ? '#2e7d32' : '#1565c0',
            fontSize: '0.8rem', fontWeight: 700, alignSelf: 'flex-start',
          }}>
          {isPaid ? '⭐ Plus Plan — Unlimited' : `Free — ${msgsRemaining} msg${msgsRemaining !== 1 ? 's' : ''} · ${wordsRemaining} words left today`}
          </div>
        </div>

        {/* Usage bar + upgrade prompt for free users */}
        {usageLoaded && !isPaid && (
          <div style={{ marginBottom: '16px' }}>
            {/* Two-row limit display */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '5px' }}>
              <span>Today's free limit</span>
              <span style={{ color: usageColor, fontWeight: 600 }}>
                {msgsRemaining}/{FREE_MSG_LIMIT} msgs &nbsp;·&nbsp; ~{wordsRemaining} words left
              </span>
            </div>
            {/* Single bar — shows whichever guard is more exhausted */}
            <div style={{ background: 'var(--border-color)', borderRadius: '6px', height: '6px' }}>
              <div style={{
                width: `${usagePct}%`, background: usageColor,
                borderRadius: '6px', height: '6px', transition: 'width 0.4s',
              }} />
            </div>
            {/* Sub-labels for each guard */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.74rem', color: 'var(--text-muted)' }}>
              <span>Messages: {msgUsed}/{FREE_MSG_LIMIT}</span>
              <span>Words: {Math.floor(tokensUsed * 0.75)}/{FREE_WORD_LIMIT}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>
                {limitReached
                  ? '⚠️ Daily limit reached. Come back tomorrow or upgrade.'
                  : 'Both limits apply — whichever runs out first.'
                }
              </p>
              {/* RAZORPAY: replace onClick with Razorpay checkout */}
              <button
                onClick={handleUpgrade}
                style={{
                  padding: '4px 12px', background: '#1565c0', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '0.78rem', fontWeight: 700,
                }}
              >
                Upgrade — ₹35/mo
              </button>
            </div>
          </div>
        )}

        {/* Chat Window */}
        <div className="chat-window" ref={chatWindowRef}>
          {messages.map((m, i) => (
            <div key={i} className={`message ${m.sender === 'user' ? 'sent' : 'received'}`}>
              {m.sender === 'bot' ? (
                <div>
                  {m.text}
                  {m.isCrisis && (
                    <div style={{ marginTop: '10px' }}>
                      <a href="tel:9152987821" style={{
                        display: 'inline-block', padding: '8px 16px',
                        background: '#1976d2', color: '#fff',
                        borderRadius: '8px', textDecoration: 'none',
                        fontSize: '0.85rem', fontWeight: 600,
                      }}>
                        📞 iCall — 9152987821
                      </a>
                    </div>
                  )}
                </div>
              ) : m.text}
            </div>
          ))}

          {loading && (
            <div className="message received" style={{ opacity: 0.7 }}>
              Saksham is thinking...
            </div>
          )}

          {/* Limit reached in-chat banner */}
          {limitReached && !isPaid && (
            <div style={{
              textAlign: 'center', padding: '16px', borderRadius: '12px',
              background: '#fff3e0', margin: '8px 0',
            }}>
              <p style={{ margin: '0 0 10px', color: '#e65100', fontSize: '0.9rem' }}>
                You've used all {FREE_MSG_LIMIT} free messages today. Come back tomorrow! 💙
              </p>
              {/* RAZORPAY: replace onClick with Razorpay checkout */}
              <button
                onClick={handleUpgrade}
                style={{
                  padding: '8px 20px', background: '#1565c0',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                }}
              >
                Upgrade to Plus — ₹35/month (Unlimited)
              </button>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="chat-input-area" style={{ flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                value={inputText}
                placeholder={limitReached && !isPaid ? `Daily limit reached — come back tomorrow 💙` : 'Type or speak your message...'}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={limitReached && !isPaid}
                style={{
                  width      : '100%',
                  paddingRight: '52px',
                  borderColor: msgTooLong ? '#ef5350' : undefined,
                }}
              />
              {inputText && (
                <span style={{
                  position : 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  fontSize : '0.72rem',
                  color    : msgTooLong ? '#ef5350' : 'var(--text-muted)',
                }}>
                  {wordCount}w
                </span>
              )}
            </div>
            <button
              onClick={handleSendMessage}
              disabled={loading || (limitReached && !isPaid) || !inputText.trim() || msgTooLong}
            >
              Send
            </button>
            <button onClick={toggleListening} className="mic-button">
              {isListening ? '🔴 Stop' : '🎤'}
            </button>
          </div>

          {/* Word count warning */}
          {msgTooLong && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#ef5350' }}>
              Message too long ({wordCount} words). Please keep it under {MAX_WORDS} words. 💙
            </p>
          )}
        </div>

        {/* Disclaimer */}
        <p className="chat-disclaimer" style={{
          marginTop: '14px', fontSize: '0.82rem',
          color: 'var(--text-muted)', textAlign: 'center',
        }}>
          ⚠️ <strong>Disclaimer:</strong> Saksham is an AI companion and may make mistakes.
          For serious mental health concerns, consult a qualified professional.
          Crisis: <strong>iCall 9152987821</strong>
        </p>
      </div>
    </div>
  );
}